import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const SESSION_KEY = 'imoney_user';
const REGISTERED_USERS_KEY = 'imoney_registered_users';

// Hydrate any users that were registered in a previous browser session
// back into the in-memory demo database on boot.
function hydrateRegisteredUsers(db) {
  try {
    const raw = localStorage.getItem(REGISTERED_USERS_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    if (!Array.isArray(saved)) return;

    const existingEmails = new Set(
      db.users.getAll().map((u) => (u.email || '').toLowerCase())
    );

    saved.forEach((entry) => {
      const email = (entry.email || '').toLowerCase();
      if (!email || existingEmails.has(email)) return;
      db.users.create(entry);
    });
  } catch (e) {
    // corrupt payload — clear it so the next register writes clean
    try { localStorage.removeItem(REGISTERED_USERS_KEY); } catch {}
  }
}

function persistRegisteredUser(newUser, rawInput) {
  try {
    const raw = localStorage.getItem(REGISTERED_USERS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    // Store the fields needed to re-create the user on next boot.
    // We persist the raw input (incl. password) plus the generated id so the
    // user can log in again after a full page reload.
    list.push({
      id: newUser.id,
      ...rawInput,
      role: newUser.role,
      referralLevel: newUser.referralLevel,
      tokenBalance: newUser.tokenBalance,
      status: newUser.status,
      referralCode: newUser.referralCode,
    });
    localStorage.setItem(REGISTERED_USERS_KEY, JSON.stringify(list));
  } catch (e) {
    // ignore — persistence is best-effort
  }
}

// Read/write the active session to either sessionStorage or localStorage
// depending on remember-me.
function readSession() {
  try {
    const local = localStorage.getItem(SESSION_KEY);
    if (local) return { user: JSON.parse(local), persistent: true };
    const session = sessionStorage.getItem(SESSION_KEY);
    if (session) return { user: JSON.parse(session), persistent: false };
  } catch (e) {
    // corrupt — wipe both
    try { localStorage.removeItem(SESSION_KEY); } catch {}
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  }
  return { user: null, persistent: false };
}

function writeSession(userData, persistent) {
  // Always clear the other bucket first so there's never an ambiguous state
  try { localStorage.removeItem(SESSION_KEY); } catch {}
  try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  if (!userData) return;
  const payload = JSON.stringify(userData);
  if (persistent) {
    localStorage.setItem(SESSION_KEY, payload);
  } else {
    sessionStorage.setItem(SESSION_KEY, payload);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isPersistent, setIsPersistent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    (async () => {
      try {
        const db = (await import('../data/demoDatabase')).default;
        hydrateRegisteredUsers(db);

        const { user: stored, persistent } = readSession();
        if (stored) {
          // Re-fetch the canonical user record from the (re-seeded) db so
          // fields like tokenBalance stay in sync after a reload.
          const fresh = db.users.getAll().find((u) => u.id === stored.id);
          setUser(fresh || stored);
          setIsPersistent(persistent);
        }
      } catch (e) {
        writeSession(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const persistUser = useCallback((userData, persistent) => {
    writeSession(userData, persistent);
    setUser(userData);
    setIsPersistent(!!persistent);
  }, []);

  // Login with email/password
  const login = useCallback(async (email, password, rememberMe = false) => {
    const db = (await import('../data/demoDatabase')).default;
    const authenticated = db.users.authenticate(email, password);
    if (authenticated) {
      persistUser(authenticated, rememberMe);
      return authenticated;
    }
    return null;
  }, [persistUser]);

  // Quick demo login by role — always session-only, never persistent
  const loginAsRole = useCallback(async (role) => {
    const db = (await import('../data/demoDatabase')).default;
    const usersOfRole = db.users.getByRole(role);
    if (usersOfRole.length > 0) {
      const demoUser = usersOfRole[0];
      persistUser(demoUser, false);
      return demoUser;
    }
    return null;
  }, [persistUser]);

  // Register new user — uses the password supplied by the form
  const register = useCallback(async (userData) => {
    const db = (await import('../data/demoDatabase')).default;

    // Guard against duplicate emails (RegisterPage already checks, but
    // defence-in-depth so direct callers don't bypass).
    const existing = db.users.getAll().find(
      (u) => u.email && userData.email && u.email.toLowerCase() === userData.email.toLowerCase()
    );
    if (existing) return null;

    if (!userData.password || userData.password.length < 6) {
      return null;
    }

    const createPayload = {
      ...userData,
      role: 'free_user',
      referralLevel: 'Silver',
      tokenBalance: 5,
      totalReferrals: 0,
      status: 'active',
      password: userData.password,
    };

    const newUser = db.users.create(createPayload);
    if (newUser) {
      // Persist raw credentials so the account survives a full page reload
      persistRegisteredUser(newUser, createPayload);
      // New users start in a regular (non-persistent) session — they can
      // explicitly tick "remember me" on next login.
      persistUser(newUser, false);
      return newUser;
    }
    return null;
  }, [persistUser]);

  // Logout
  const logout = useCallback(() => {
    persistUser(null, false);
  }, [persistUser]);

  // Update current user data
  const updateUser = useCallback(async (fields) => {
    if (!user) return null;
    const db = (await import('../data/demoDatabase')).default;
    const updated = db.users.update(user.id, fields);
    if (updated) {
      persistUser(updated, isPersistent);
      return updated;
    }
    return null;
  }, [user, isPersistent, persistUser]);

  // Verify OTP (demo: accepts "123456")
  const verifyOTP = useCallback((otp) => {
    return otp === '123456';
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginAsRole,
    register,
    logout,
    updateUser,
    verifyOTP,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
